import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Button from "components/Button";
import ChunkUploadFile from "components/ChunkUploadFile";
import Modal from "components/Modal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { routes, UPLOAD_DOCUMENT_URLS } from "constants/routes";
import { selectUserDetails } from "store/user/selector";
import styles from "./uploadDocuments.style.module.scss";
import { useToggle } from "usehooks-ts";
import { useLocation, useNavigate } from "react-router-dom";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IFRAttachment, IUpdatedFRAttachment } from "models/HaulPay";
import AttachmentItem from "components/AttachmentItem";
import { deleteFRAttachmentAPI } from "services/api/haulpay";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";

interface SharedValue {
  fundingRequestId?: string;
  contractType?: string;
}

export default function UploadDocuments({ fundingRequestId, contractType }: Readonly<SharedValue>) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [uploadedFiles, setUploadedFiles] = useState<IFRAttachment[]>([]);
  const [updatingFile, setUpdatingFile] = useState<IFRAttachment[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useToggle(false);
  const userDetails = useSelector(selectUserDetails);

  const goToConfirmation = useCallback(() => {
    const hasRateConfirmation = uploadedFiles.find((file) =>
      file.category.includes("Rate Confirmation")
    );
    const hasSignedBill = uploadedFiles.find(
      (file) =>
        file.category.includes("signed_bol_delivered") ||
        file.category.includes("signed_bol_pickup") ||
        file.category.includes("Signed Bill of Lading (Delivered)") ||
        file.category.includes("Signed Bill of Lading (Pick-Up)")
    );
    const hasTruckOrderUsed = uploadedFiles.find((file) =>
      file.category.includes("Truck Order Not Used")
    );
    if (
      (hasRateConfirmation && hasSignedBill) ||
      hasTruckOrderUsed ||
      contractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE
    ) {
      showSuccessMessage("Funding Request has been successfully created.");
      return navigate(
        pathname.includes("haulpay") ? routes.HAULPAY_BROKER_MANAGE_PAY : routes.LOADS_POSTED_ALL
      );
    }
    return setIsConfirmModalOpen();
  }, [uploadedFiles]);
  const deleteFile = async (fileId: string) => {
    if (fundingRequestId) {
      try {
        await deleteFRAttachmentAPI(fundingRequestId, fileId);
        setUploadedFiles(uploadedFiles.filter((item: IFRAttachment) => item.id !== fileId));
      } catch {
        showErrorMessage("Failed to delete attachment.");
      }
    }
  };

  const updatedFileId = useMemo(() => {
    if (updatingFile.length > 0) return updatingFile[0].id;
  }, [updatingFile]);

  return (
    <div className={styles.payDocumentsContainer}>
      <div className={styles.body}>
        <h4 className={styles.header}>Documents</h4>
        <div className={styles.uploadWrapper}>
          <ChunkUploadFile
            className="desktop:flex desktop:flex-col desktop:gap-4 desktop:text-lg "
            multiple
            maxSizeBytes={25 * 1024 * 1024}
            urlCallback={
              UPLOAD_DOCUMENT_URLS({
                factoring_id: userDetails.factoring_id,
                funding_request_id: fundingRequestId
              }).FR_ATTACHMENT
            }
            onUploadFinished={(files: any, fileId: string) => {
              const newFiles: IFRAttachment[] = files.map((file: any) => ({
                fileId: fileId,
                id: file.id,
                filename: file.name,
                category: file.categories.split(","),
                downloadUrl: file.preview_url,
                visibleTo: file.visible_to,
                type: file.fileType,
                size: file.fileSize
              }));
              setUploadedFiles([
                ...uploadedFiles.filter((item: IFRAttachment) => item.id !== updatedFileId),
                ...newFiles
              ]);
            }}
            updatedFile={
              updatingFile.map((file) => ({
                ...file,
                frId: fundingRequestId
              })) as IUpdatedFRAttachment[]
            }
            setFile={setUpdatingFile}
            visibility
          />
          {uploadedFiles.map((file) => (
            <div key={file.id} className="-mx-2">
              <AttachmentItem file={file} deleteFile={deleteFile} setFile={setUpdatingFile} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="outline" onClick={setIsConfirmModalOpen}>
          Complete Later
        </Button>
        <Button onClick={goToConfirmation}>Next</Button>
      </div>

      <Modal
        isOpen={isConfirmModalOpen}
        closeModal={setIsConfirmModalOpen}
        wrapperClass="!my-auto desktop:!min-h-fit desktop:!w-1/2 text-sm"
        dialogClass="!bg-foreground !px-3 !py-4 gap-2.5"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <div className="flex items-center gap-1 text-xl font-bold text-primary">
          <ExclamationSVGIcon />
          <span>Confirm</span>
        </div>
        <p className="pt-1.5 font-medium">
          Are you sure you want to create an Incomplete Funding Request? You can navigate to
          Incomplete in Manage Pay to add documents later if you do.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            rounded
            className="!w-20 text-sm !h-9 self-end"
            onClick={setIsConfirmModalOpen}>
            No
          </Button>
          <Button
            rounded
            className="!w-20 text-sm !h-9 self-end"
            onClick={() => {
              navigate(routes.HAULPAY_BROKER_MANAGE_PAY);
              showSuccessMessage("Funding Request has been successfully created.");
            }}>
            Yes
          </Button>
        </div>
      </Modal>
    </div>
  );
}
